<template>
  <v-container fluid class="pa-2 page-layout">
    <v-row no-gutters>
      <!-- Filters Sidebar -->
      <v-col cols="12" md="4" lg="3" class="pa-3">
        <h1 class="page-title">Find Providers</h1>
        
        <!-- Search by Name -->
        <v-text-field
          v-model="searchTerm"
          label="Search for Name"
          outlined
          clearable
          dense
          class="mb-3"
          @input="debouncedSearch"
        ></v-text-field>
        
        <!-- Specialty Filter -->
        <v-select
          v-model="selectedSpecialty"
          :items="specialties"
          label="Provider Specialty"
          outlined
          clearable
          dense
          class="mb-3"
          @change="searchProviders"
        ></v-select>
        
        <!-- Location Filter -->
        <v-text-field
          v-model="locationFilter"
          label="Location"
          outlined
          clearable
          dense
          class="mb-3"
          placeholder="City, State, or ZIP"
          @input="debouncedSearch"
        ></v-text-field>
        
        <!-- Results Count -->
        <v-card class="mt-4" outlined>
          <v-card-text>
            <div class="text-h6">{{ providers.length }} providers found</div>
            <div class="text-caption mt-1">
              <span v-if="providersWithCoordsCount > 0" class="success--text">
                {{ providersWithCoordsCount }} with coordinates
              </span>
              <span v-if="providersWithoutCoordsCount > 0" class="warning--text ml-2">
                {{ providersWithoutCoordsCount }} without coordinates
              </span>
            </div>
          </v-card-text>
        </v-card>
        
        <!-- Provider List -->
        <v-list class="mt-4" max-height="500" style="overflow-y: auto;">
          <v-list-item
            v-for="provider in providers"
            :key="providerRowKey(provider)"
            @click="selectProvider(provider)"
            :class="{ 'primary lighten-4': selectedProvider && isSameProviderRow(selectedProvider, provider) }"
          >
            <v-list-item-content>
              <v-list-item-title>{{ provider.PROVIDER_NAME }}</v-list-item-title>
              <v-list-item-subtitle>
                <div>{{ provider.PRIMARY_SPECIALTY }}</div>
                <div v-if="provider.SERVICE_LOCATION_CITY">
                  {{ provider.SERVICE_LOCATION_CITY }}, {{ provider.SERVICE_LOCATION_STATE }}
                </div>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-col>
      
      <!-- Map -->
      <v-col cols="12" md="8" lg="9">
        <div id="map" style="width: 100%; height: 100vh;"></div>
      </v-col>
    </v-row>
    
    <!-- Provider Details Dialog -->
    <v-dialog v-model="showProviderDialog" max-width="600">
      <v-card v-if="selectedProvider">
        <v-card-title>
          {{ selectedProvider.PROVIDER_NAME }}
          <v-spacer></v-spacer>
          <v-btn icon @click="showProviderDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-list>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>Provider ID</v-list-item-title>
                <v-list-item-subtitle>{{ selectedProvider.PROVIDER_ID }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="selectedProvider.PRIMARY_SPECIALTY">
              <v-list-item-content>
                <v-list-item-title>Specialty</v-list-item-title>
                <v-list-item-subtitle>{{ selectedProvider.PRIMARY_SPECIALTY }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="selectedProvider.GROUP_NAME">
              <v-list-item-content>
                <v-list-item-title>Group</v-list-item-title>
                <v-list-item-subtitle>{{ selectedProvider.GROUP_NAME }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="selectedProvider.SERVICE_LOCATION_ADDRESS_1">
              <v-list-item-content>
                <v-list-item-title>Address</v-list-item-title>
                <v-list-item-subtitle>
                  {{ selectedProvider.SERVICE_LOCATION_ADDRESS_1 }}<br>
                  <span v-if="selectedProvider.SERVICE_LOCATION_CITY">
                    {{ selectedProvider.SERVICE_LOCATION_CITY }}, {{ selectedProvider.SERVICE_LOCATION_STATE }} {{ selectedProvider.SERVICE_LOCATION_ZIP }}
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="selectedProvider.BILLING_PHONE">
              <v-list-item-content>
                <v-list-item-title>Phone</v-list-item-title>
                <v-list-item-subtitle>{{ selectedProvider.BILLING_PHONE }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios';
import { debounce } from 'lodash';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

export default {
  name: 'ProviderSearchMap',
  data() {
    const data = {
      mapboxAccessToken: 'pk.eyJ1IjoiamJmb2xpdmVpcmEiLCJhIjoiY21naDJxd3lqMGRucjJtcHk0OXBwYmVpMCJ9.vZnIqcYZjZj05ZARV8jd8w',
      providers: [],
      specialties: [],
      selectedSpecialty: null,
      locationFilter: '',
      mapSearchRadiusMiles: 15,
      searchTerm: '',
      selectedProvider: null,
      showProviderDialog: false,
      markers: [],
      popups: [],
      user: null,
      providersWithCoordsCount: 0,
      providersWithoutCoordsCount: 0,
    };
    
    // CRITICAL FIX: Store Mapbox-related objects outside Vue reactivity to prevent DevTools crashes
    // Mapbox objects are extremely deeply nested and cause infinite loops when DevTools inspects them
    let mapInstance = null;
    let markersInstance = [];
    let popupsInstance = [];
    
    Object.defineProperty(data, 'map', {
      get() { return mapInstance; },
      set(value) { mapInstance = value; },
      enumerable: false,
      configurable: true
    });
    
    Object.defineProperty(data, 'markers', {
      get() { return markersInstance; },
      set(value) { markersInstance = Array.isArray(value) ? value : []; },
      enumerable: false,
      configurable: true
    });
    
    Object.defineProperty(data, 'popups', {
      get() { return popupsInstance; },
      set(value) { popupsInstance = Array.isArray(value) ? value : []; },
      enumerable: false,
      configurable: true
    });
    
    return data;
  },
  mounted() {
    mapboxgl.accessToken = this.mapboxAccessToken;
    this.getUser();
    this.getSpecialties();
    this.$nextTick(() => {
      this.initMap();
    });
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove();
    }
  },
  methods: {
    providerRowKey(provider) {
      if (!provider) return '';
      const pract =
        provider.PRACTICE_ID !== undefined &&
        provider.PRACTICE_ID !== null &&
        String(provider.PRACTICE_ID).trim() !== ''
          ? String(provider.PRACTICE_ID)
          : '';
      return `${provider.PROVIDER_ID}::${pract}`;
    },
    isSameProviderRow(a, b) {
      if (!a || !b) return false;
      const pa =
        a.PRACTICE_ID !== undefined && a.PRACTICE_ID !== null && String(a.PRACTICE_ID).trim() !== ''
          ? String(a.PRACTICE_ID)
          : '';
      const pb =
        b.PRACTICE_ID !== undefined && b.PRACTICE_ID !== null && String(b.PRACTICE_ID).trim() !== ''
          ? String(b.PRACTICE_ID)
          : '';
      return String(a.PROVIDER_ID) === String(b.PROVIDER_ID) && pa === pb;
    },
    async getUser() {
      try {
        console.log('👤 Getting user...');
        const response = await axios.get('/api/user');
        if (response.data && response.data.length > 0) {
          this.user = response.data[0];
          console.log('✅ User loaded:', this.user.username, this.user.role);
          // Wait a bit for map to initialize, then search
          setTimeout(() => {
            this.searchProviders();
          }, 1000);
        }
      } catch (error) {
        console.error('❌ Error getting user:', error);
        this.$router.push('/login');
      }
    },
    async getSpecialties() {
      try {
        const response = await axios.get('/api/providers/specialties');
        this.specialties = response.data;
      } catch (error) {
        console.error('Error fetching specialties:', error);
      }
    },
    debouncedSearch: debounce(function() {
      this.searchProviders();
    }, 500),
    async searchProviders() {
      try {
        const params = {
          specialty: this.selectedSpecialty || undefined,
          location: this.locationFilter || undefined,
          search: this.searchTerm || undefined,
        };
        const locTrim = (this.locationFilter && String(this.locationFilter).trim()) || '';
        if (locTrim) {
          params.radiusMiles = this.mapSearchRadiusMiles;
          if (this.map) {
            try {
              const c = this.map.getCenter();
              if (c && Number.isFinite(c.lng) && Number.isFinite(c.lat)) {
                params.proximityLon = c.lng;
                params.proximityLat = c.lat;
              }
            } catch (_e) {
              /* ignore */
            }
          }
        }

        // Remove undefined params
        Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
        
        const queryString = new URLSearchParams(params).toString();
        const url = `/api/providers/mapsearch/all${queryString ? '?' + queryString : ''}`;
        
        console.log('🔍 Searching providers with URL:', url);
        const response = await axios.get(url);
        console.log('✅ Providers response:', response.data);
        console.log('✅ Number of providers:', response.data ? response.data.length : 0);
        
        this.providers = response.data || [];
        
        if (this.providers.length > 0) {
          console.log('📋 Sample provider:', this.providers[0]);
          console.log('📋 Provider has coordinates:', 
            this.providers[0].SERVICE_LOCATION_LAT && this.providers[0].SERVICE_LOCATION_LON);
          console.log('📋 Provider address fields:', {
            SERVICE_LOCATION_ADDRESS_1: this.providers[0].SERVICE_LOCATION_ADDRESS_1,
            SERVICE_LOCATION_CITY: this.providers[0].SERVICE_LOCATION_CITY,
            SERVICE_LOCATION_STATE: this.providers[0].SERVICE_LOCATION_STATE,
            SERVICE_LOCATION_ZIP: this.providers[0].SERVICE_LOCATION_ZIP,
            REMIT_TO_ADDRESS_1: this.providers[0].REMIT_TO_ADDRESS_1,
            REMIT_TO_CITY: this.providers[0].REMIT_TO_CITY,
          });
        }
        
        this.updateMapMarkers();
      } catch (error) {
        console.error('❌ Error searching providers:', error);
        console.error('❌ Error response:', error.response);
        this.providers = [];
      }
    },
    initMap() {
      if (!mapboxgl.accessToken) {
        console.error('❌ Mapbox access token not set');
        return;
      }

      console.log('🗺️ Initializing map...');
      this.map = new mapboxgl.Map({
        container: 'map',
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [-80.1918, 25.7617], // Default to Miami, FL
        zoom: 10
      });

      this.map.on('load', () => {
        console.log('✅ Map loaded successfully');
        // Only update markers if we already have providers loaded
        if (this.providers.length > 0) {
          this.updateMapMarkers();
        }
      });

      this.map.on('error', (e) => {
        console.error('❌ Map error:', e);
      });

      // Add navigation controls
      this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    },
    clearMarkers() {
      // Remove all existing markers and popups
      this.markers.forEach(marker => marker.remove());
      this.popups.forEach(popup => popup.remove());
      this.markers = [];
      this.popups = [];
    },
    async updateMapMarkers() {
      if (!this.map) {
        console.log('⚠️ Map not initialized yet');
        return;
      }
      
      // Wait for map to be loaded
      if (!this.map.isStyleLoaded()) {
        console.log('⏳ Waiting for map to load...');
        this.map.once('load', () => {
          this.updateMapMarkers();
        });
        return;
      }

      console.log('🗺️ Updating map markers for', this.providers.length, 'providers');
      this.clearMarkers();

      if (this.providers.length === 0) {
        console.log('⚠️ No providers to display');
        return;
      }

      // Group providers by coordinates to handle duplicates
      const providersWithCoords = [];
      const providersWithoutCoords = [];

      this.providers.forEach(provider => {
        const lat = provider.SERVICE_LOCATION_LAT;
        const lon = provider.SERVICE_LOCATION_LON;
        
        // Check if coordinates exist and are valid numbers
        if (lat != null && lon != null && lat !== '' && lon !== '' && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lon))) {
          providersWithCoords.push(provider);
        } else {
          providersWithoutCoords.push(provider);
          // Log why provider was skipped
          if (providersWithoutCoords.length <= 5) { // Only log first 5 to avoid spam
            console.log(`⚠️ Provider ${provider.PROVIDER_ID} skipped - lat: ${lat}, lon: ${lon}`);
          }
        }
      });
      
      console.log('📍 Providers with valid coordinates:', providersWithCoords.length);
      console.log('📍 Providers without valid coordinates:', providersWithoutCoords.length);
      
      // Update counts for display
      this.providersWithCoordsCount = providersWithCoords.length;
      this.providersWithoutCoordsCount = providersWithoutCoords.length;
      
      if (providersWithCoords.length > 0) {
        console.log('📍 Sample provider with coords:', {
          id: providersWithCoords[0].PROVIDER_ID,
          name: providersWithCoords[0].PROVIDER_NAME,
          lat: providersWithCoords[0].SERVICE_LOCATION_LAT,
          lon: providersWithCoords[0].SERVICE_LOCATION_LON
        });
      }
      
      // Log all providers with coordinates for debugging
      if (providersWithCoords.length > 0 && providersWithCoords.length <= 10) {
        console.log('📍 All providers with coordinates:');
        providersWithCoords.forEach((p, idx) => {
          console.log(`   ${idx + 1}. ${p.PROVIDER_ID}: ${p.SERVICE_LOCATION_LAT}, ${p.SERVICE_LOCATION_LON}`);
        });
      }

      // Add markers for providers with coordinates
      this.addMarkersForProviders(providersWithCoords);

      // Fit map to show all markers (before geocoding)
      if (providersWithCoords.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        providersWithCoords.forEach(provider => {
          bounds.extend([parseFloat(provider.SERVICE_LOCATION_LON), parseFloat(provider.SERVICE_LOCATION_LAT)]);
        });
        this.map.fitBounds(bounds, {
          padding: 50,
          maxZoom: 15
        });
      }

      // Note: Providers without coordinates should be geocoded using the backend script
      // Run: npm run geocode-providers
      if (providersWithoutCoords.length > 0) {
        console.log(`⚠️ ${providersWithoutCoords.length} providers without coordinates`);
        console.log('   Run the geocoding script to add coordinates: npm run geocode-providers');
      }
    },
    addMarkersForProviders(providers) {
      if (!this.map || !this.map.isStyleLoaded()) {
        console.log('⚠️ Map not ready, cannot add markers');
        return;
      }
      
      console.log(`📍 Adding ${providers.length} markers to map`);
      
      providers.forEach(provider => {
        // Check if marker already exists
        const existingMarker = this.markers.find(m => {
          const markerProvider = m._provider;
          return markerProvider && this.isSameProviderRow(markerProvider, provider);
        });
        
        if (existingMarker) {
          console.log(`⏭️ Skipping duplicate marker for ${this.providerRowKey(provider)}`);
          return; // Skip if marker already exists
        }
        
        // Validate coordinates
        const lat = parseFloat(provider.SERVICE_LOCATION_LAT);
        const lon = parseFloat(provider.SERVICE_LOCATION_LON);
        
        if (isNaN(lat) || isNaN(lon)) {
          console.log(`⚠️ Invalid coordinates for provider ${provider.PROVIDER_ID}:`, 
            'lat:', provider.SERVICE_LOCATION_LAT, 'lon:', provider.SERVICE_LOCATION_LON);
          return;
        }
        
        // Validate coordinate ranges (latitude: -90 to 90, longitude: -180 to 180)
        if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
          console.log(`⚠️ Coordinates out of range for provider ${provider.PROVIDER_ID}:`, lat, lon);
          return;
        }
        
        console.log(`📍 Adding marker for ${provider.PROVIDER_NAME} at ${lat}, ${lon}`);
        
        const el = document.createElement('div');
        el.className = 'marker';
        el.style.width = '30px';
        el.style.height = '30px';
        el.style.borderRadius = '50%';
        el.style.backgroundColor = '#1976d2';
        el.style.border = '3px solid white';
        el.style.cursor = 'pointer';
        el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';

        const marker = new mapboxgl.Marker(el)
          .setLngLat([lon, lat])
          .addTo(this.map);

        // Store provider reference in marker for duplicate checking
        marker._provider = provider;

        // Create popup
        const popup = new mapboxgl.Popup({ offset: 25 })
          .setHTML(`
            <div style="padding: 8px;">
              <strong>${provider.PROVIDER_NAME || 'N/A'}</strong><br>
              ${provider.PRIMARY_SPECIALTY ? provider.PRIMARY_SPECIALTY : ''}<br>
              <small>${provider.SERVICE_LOCATION_CITY || ''}${provider.SERVICE_LOCATION_STATE ? ', ' + provider.SERVICE_LOCATION_STATE : ''}</small>
            </div>
          `);

        marker.setPopup(popup);

        // Add click handler
        el.addEventListener('click', () => {
          this.selectProvider(provider);
        });

        this.markers.push(marker);
      });
      
      console.log(`✅ Total markers on map: ${this.markers.length}`);
    },
    selectProvider(provider) {
      this.selectedProvider = provider;
      this.showProviderDialog = true;
      
      // Center map on provider location if coordinates are available
      if (provider.SERVICE_LOCATION_LAT && provider.SERVICE_LOCATION_LON && this.map) {
        this.map.flyTo({
          center: [parseFloat(provider.SERVICE_LOCATION_LON), parseFloat(provider.SERVICE_LOCATION_LAT)],
          zoom: 15
        });
      }
    },
  },
};
</script>

<style scoped>
#map {
  min-height: 600px;
  width: 100%;
  height: 100vh;
}

.marker {
  background-color: #1976d2;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 3px solid white;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
}

.marker:hover {
  transform: scale(1.1);
  transition: transform 0.2s;
}
</style>

